import { PermissionGate } from '@/components/auth/permission-gate';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { Settings } from 'lucide-react';
import api from '@/lib/api';
import type { AbsenceRequest } from '@hygge/shared';

const tabs = [
  {
    id: 'calendar',
    label: 'CALENDAR',
    href: '/absences/calendar',
  },
  {
    id: 'schedule',
    label: 'SCHEDULE',
    href: '/absences/schedule',
  },
  {
    id: 'requests',
    label: 'REQUESTS',
    href: '/absences/request',
  },
  {
    id: 'balances',
    label: 'BALANCES',
    href: '/absences/balances',
  },
] as const;

export function AbsencesPageHeader() {
  const location = useLocation();
  const navigate = useNavigate();

  const { data: pendingRequests } = useQuery({
    queryKey: ['absence-requests', 'pending-count'],
    queryFn: async () => {
      const { data } = await api.get<AbsenceRequest[]>(
        '/absence-requests/pending-for-approval',
      );
      return data;
    },
    refetchInterval: 30000,
  });

  const pendingCount = pendingRequests?.length ?? 0;

  const canViewBalances = usePermission('absences.balances.view');
  const visibleTabs = tabs.filter((t) => t.id !== 'balances' || canViewBalances);

  const isRequestsPage = location.pathname === '/absences/request';
  const isCalendarPage = location.pathname === '/absences/calendar';
  const isSchedulePage = location.pathname === '/absences/schedule';
  const isBalancesPage = location.pathname === '/absences/balances';

  return (
    <header className="sticky top-14 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex min-h-12 items-center justify-between px-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Home</span>
          <span>/</span>
          <span className="font-medium text-foreground">Absences</span>
          {isRequestsPage && (
            <>
              <span>/</span>
              <span className="font-medium text-foreground">Requests</span>
            </>
          )}
          {isCalendarPage && (
            <>
              <span>/</span>
              <span className="font-medium text-foreground">Calendar</span>
            </>
          )}
          {isSchedulePage && (
            <>
              <span>/</span>
              <span className="font-medium text-foreground">Schedule</span>
            </>
          )}
          {isBalancesPage && (
            <>
              <span>/</span>
              <span className="font-medium text-foreground">Balances</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-4">
        <nav className="-mb-px flex gap-6" aria-label="Absences navigation">
          {visibleTabs.map((tab) => {
            const isActive = location.pathname === tab.href;
            return (
              <Link
                key={tab.id}
                to={tab.href}
                className={cn(
                  'relative pb-3 pt-3 text-sm font-normal tracking-wide transition-colors',
                  isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <span className="flex items-center gap-2">
                  {tab.label}
                  {tab.id === 'requests' && pendingCount > 0 && (
                    <span
                      className={cn(
                        'text-xs px-1.5 py-0.5 rounded-full',
                        isActive
                          ? 'bg-hygge/10 text-hygge'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {pendingCount}
                    </span>
                  )}
                </span>
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-hygge rounded-t-full" />
                )}
              </Link>
            );
          })}
        </nav>

        <PermissionGate permission="absences.types.manage">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
                  onClick={() => navigate({ to: '/settings/absence-types' })}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Absence type settings</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </PermissionGate>
        </div>
      </div>
    </header>
  );
}
